import { useState } from 'react';

import DocumentRequirementStep from './DocumentRequirementStep/DocumentRequirementStep';
import LoanSummaryRevamp from './LoanSummaryRevamp';
import BankStatements from './RequirementTypes/BankStatements';
import styles from './TradingHistoryRevamp.module.css';
import { useGetApplicationQuery } from '../../../api/lendingApi/lendingApiQueries';
import { getDurationInMonths } from '../../../helpers/product.helpers';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useOffersUK } from '../../../hooks/useOffers/useOffersUK';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { TradingHistoryLoader } from '../TradingHistoryLoader';
import { requirementsTitle } from '../TradingHistoryUK/ShareRequirements/ShareRequirements.helper';

const DOCUMENT_REQUIREMENTS: Record<string, React.ElementType> = {
  bank_statement: BankStatements,
};

const TradingHistoryRevamp = () => {
  const { formatCurrency } = useNumberFormat();
  const [activeStepIndex] = useState(0);

  const { application } = useGetApplicationQuery();

  const { documentRequirements, isLoadingOffers, offers } = useOffersUK({
    enabled: true,
  });

  const requestedAmount = application?.requested_products?.[0]?.amount;

  const suggestedOffer = offers?.find((offer) => offer.is_suggested);
  const estimatedAmount = suggestedOffer?.max_amount;

  const maxTermLength = getDurationInMonths(suggestedOffer?.duration);

  const isEstimatedAmountSmaller =
    Number(estimatedAmount) < Number(requestedAmount);

  const displayAmount = isEstimatedAmountSmaller
    ? requestedAmount
    : estimatedAmount;

  if (isLoadingOffers) {
    return <TradingHistoryLoader />;
  }

  const requiredDocumentRequirements =
    documentRequirements?.filter((doc) => !doc.is_optional) || [];

  return (
    <div className={styles.container}>
      <ProgressBar />
      <div className={styles.content}>
        <LoanSummaryRevamp
          displayAmount={displayAmount}
          maxTermLength={maxTermLength}
          isEstimatedAmountSmaller={isEstimatedAmountSmaller}
        />
        <h1 className={styles.heading}>
          You’re eligible for up to{' '}
          <span className={styles.amount}>
            {formatCurrency(displayAmount)}*
          </span>
        </h1>
        <p className={styles.subheading}>
          We just need a little bit more information to be able to make the most
          accurate assessment.
        </p>
        {requiredDocumentRequirements.length > 0 && (
          <div className={styles.steps}>
            {requiredDocumentRequirements.map((requirement, index) => {
              if (!DOCUMENT_REQUIREMENTS[requirement.type]) return null;

              const RequirementContent =
                DOCUMENT_REQUIREMENTS[requirement.type];
              const title = requirementsTitle(
                requirement.type,
                requirement.quantifier?.value,
              );

              if (!title) return null;

              return (
                <DocumentRequirementStep
                  key={requirement.type}
                  stepNumber={index + 1}
                  title={title}
                  isComplete={requirement.status === 'pending'}
                  isActive={index === activeStepIndex}
                >
                  <RequirementContent
                    resolutionMethods={requirement.resolution_methods}
                  />
                </DocumentRequirementStep>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingHistoryRevamp;
